/**
 * RecordNotesListTab — wraps RecordObjectListTab with note-specific config:
 * the note list columns and the "Add task" / "Add note" create controls,
 * which sit at the far right of the list toolbar (`toolbarTrailing`) — the
 * embedded list drops its own page-title action row, so the toolbar is the
 * natural home for them. `onCreateClick` is still wired so the empty-state
 * "Create your first note" button works too. (Notes no longer carry a due,
 * so the former "Add reminder" — a dated note — is now "Add task", opening
 * the task composer linked to this record.)
 */

import { ClipboardCheck, Plus } from '@untitledui/icons';
import { useMemo } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { Button } from '@/lib/base/buttons/button';

import { noteListConfig } from '../../list/config/noteListConfig';
import { isMemoEntityType } from '../memo/notes.utils';
import { useNoteTabActions } from '../notes/useNoteTabActions';
import { useHasWritableTaskAssociation } from '../task/useHasTaskAssociation';
import { useTaskTabActions } from '../task/useTaskTabActions';
import { COMPACT_TOOLBAR_BUTTON_CLASS } from './HeaderActions';
import { RecordObjectListTab } from './RecordObjectListTab';

// Match the slim labeled-button scale used by "Draft email" and other toolbar
// buttons in this view (12px icons, text-xs, medium weight, tight padding) so
// the create actions sit lightly in the list toolbar instead of towering over
// the search/filter controls next to them.
const COMPACT_CREATE_BUTTON_CLASS = `${COMPACT_TOOLBAR_BUTTON_CLASS} !px-2 !text-xs`;

interface RecordNotesListTabProps {
  entityId: string;
  /** The host record's entity_type_name, passed as entityTypeName to the composer. */
  hostEntityType: string;
  countLabel?: string;
  countLabelSingular?: string;
}

export function RecordNotesListTab({
  entityId,
  hostEntityType,
  countLabel,
  countLabelSingular,
}: RecordNotesListTabProps) {
  const { startCreatingNote, canCreateNote } = useNoteTabActions(
    entityId,
    hostEntityType
  );
  const { startCreatingTask, canCreateTask, isStageLoading } =
    useTaskTabActions(entityId, hostEntityType);

  // Gate "Add task" on a USER-WRITABLE ahoy_task association to the host, not
  // just the create permission. Two halves matter: a memo host (ahoy_note /
  // ahoy_task) is a task itself and never links tasks, and any other host
  // lacking a writable ahoy_task association has no rule the composer can write
  // (an internal transitive rule is dropped on submit — see
  // useHasWritableTaskAssociation). Either way, opening "Add task" would pre-link
  // the task to a host whose link the schema won't accept, so require the strict
  // predicate. (The Tasks section still RENDERS for such hosts via the broad
  // `entityTypeHasTaskAssociation` — that is the render/create split.)
  const { currentOrg } = useOrg();
  const hasWritableTaskAssociation = useHasWritableTaskAssociation(
    currentOrg?.id,
    hostEntityType
  );
  const canCreateTaskForHost =
    canCreateTask &&
    hasWritableTaskAssociation &&
    !isMemoEntityType(hostEntityType);

  // Right-aligned create actions, next to the sibling Emails/Meetings tabs'
  // Search/Filter controls. Each create button drops out without its own
  // create permission.
  const toolbarTrailing = useMemo(
    () => (
      <>
        {canCreateTaskForHost && (
          <Button
            size='sm'
            color='secondary'
            iconLeading={ClipboardCheck}
            onClick={startCreatingTask}
            aria-label='Add task'
            className={COMPACT_CREATE_BUTTON_CLASS}
            // Disabled while the default task stage is still resolving:
            // startCreatingTask no-ops in that window, so an enabled button
            // would silently do nothing. Enables once the stage resolves
            // (including a genuinely stage-less org, where it stays creatable).
            isDisabled={isStageLoading}
            // Explains the transient disable; the base Button suppresses this
            // tooltip whenever the button is enabled.
            disabledTooltip='Loading…'
          >
            Add task
          </Button>
        )}
        {canCreateNote && (
          <Button
            size='sm'
            color='primary'
            iconLeading={Plus}
            onClick={startCreatingNote}
            aria-label='Add note'
            className={COMPACT_CREATE_BUTTON_CLASS}
          >
            Add note
          </Button>
        )}
      </>
    ),
    [
      canCreateNote,
      canCreateTaskForHost,
      startCreatingTask,
      startCreatingNote,
      isStageLoading,
    ]
  );

  return (
    <RecordObjectListTab
      baseConfig={noteListConfig}
      entityId={entityId}
      hostEntityType={hostEntityType}
      countLabel={countLabel}
      countLabelSingular={countLabelSingular}
      onCreateClick={startCreatingNote}
      toolbarTrailing={toolbarTrailing}
    />
  );
}
